import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MapPin, ShieldAlert, RefreshCw, Unlock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const OVERRIDE_KEY = "vlog_kiosk_override";
const DEVICE_KEY = "vlog_kiosk_device";
const KIOSK_ROLES = ["admin", "corporate_admin", "officer"];

const deviceId = () => {
  let d = localStorage.getItem(DEVICE_KEY);
  if (!d) { d = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem(DEVICE_KEY, d); }
  return d;
};

const Shell = ({ icon: Icon, title, children, testid }) => (
  <div className="flex min-h-screen items-center justify-center bg-primary p-6" data-testid={testid}>
    <div className="w-full max-w-md rounded-2xl border border-white/20 bg-white/10 p-8 text-center shadow-2xl backdrop-blur">
      <Icon className={`mx-auto h-10 w-10 text-white ${Icon === Loader2 ? "animate-spin" : ""}`} />
      <h1 className="font-display mt-4 text-2xl font-bold text-white">{title}</h1>
      <div className="mt-3 text-sm text-white/85">{children}</div>
    </div>
  </div>
);

const VERIFIED_KEY = "kiosk_geo_verified";
const VERIFIED_TTL = 12 * 60 * 60 * 1000;
// Location is verified once when the kiosk is launched; the result is kept for the session so later screens never re-check.
const recentlyVerified = () => {
  try { const v = JSON.parse(sessionStorage.getItem(VERIFIED_KEY) || "null"); return !!v && Date.now() - v.ts < VERIFIED_TTL && v.uid; } catch { return false; }
};

export default function KioskGuard({ children }) {
  const { user } = useAuth();
  const allowedRole = KIOSK_ROLES.includes(user?.role);
  const cached = allowedRole && recentlyVerified() === user?.id;
  const [state, setState] = useState({ phase: cached ? "ok" : allowedRole ? "checking" : "denied-role" });

  const verify = useCallback(async () => {
    setState({ phase: "checking" });
    const ok = () => { sessionStorage.setItem(VERIFIED_KEY, JSON.stringify({ ts: Date.now(), uid: user?.id })); setState({ phase: "ok" }); };
    let cfg;
    try {
      cfg = (await api.get("/kiosk/access")).data;
    } catch (e) {
      setState({ phase: "error", message: formatApiErrorDetail(e.response?.data?.detail) });
      return;
    }
    if (!cfg.allowed_role) { setState({ phase: "denied-role" }); return; }
    if (!cfg.geofence_enabled) { ok(); return; }

    const send = async (coords) => {
      const { data } = await api.post("/kiosk/access/check", {
        lat: coords?.latitude ?? 0, lng: coords?.longitude ?? 0, accuracy_m: Math.min(coords?.accuracy || 0, 99999),
        device_id: deviceId(), override_token: localStorage.getItem(OVERRIDE_KEY) || "",
      });
      if (data.ok) ok();
      else setState({ phase: "off-site", ...data, can_override: cfg.can_override });
    };

    // A still-valid device override skips the GPS prompt entirely.
    const ovr = localStorage.getItem(OVERRIDE_KEY);
    if (ovr) {
      try {
        const { data } = await api.post("/kiosk/access/check", { lat: 0, lng: 0, accuracy_m: 0, device_id: deviceId(), override_token: ovr });
        if (data.ok) { ok(); return; }
        localStorage.removeItem(OVERRIDE_KEY);
      } catch { localStorage.removeItem(OVERRIDE_KEY); }
    }
    if (!navigator.geolocation) { setState({ phase: "no-gps", can_override: cfg.can_override, ...cfg }); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => send(pos.coords).catch((e) => setState({ phase: "error", message: formatApiErrorDetail(e.response?.data?.detail), can_override: cfg.can_override })),
      () => setState({ phase: "no-gps", can_override: cfg.can_override, ...cfg }),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 },
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => { if (allowedRole && !cached) verify(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [allowedRole]);

  // Keep the token fresh so an all-day station never expires.
  useEffect(() => {
    if (state.phase !== "ok") return undefined;
    const refresh = () => api.post("/auth/refresh").then((r) => { if (r.data?.token) localStorage.setItem("vlog_token", r.data.token); }).catch(() => {});
    refresh();
    const iv = setInterval(refresh, 60 * 60 * 1000);
    return () => clearInterval(iv);
  }, [state.phase]);

  const grantOverride = async () => {
    try {
      const { data } = await api.post("/kiosk/access/override", { device_id: deviceId(), reason: "Opened kiosk off-site" });
      localStorage.setItem(OVERRIDE_KEY, data.token);
      toast.success("This device can open the kiosk for the next 12 hours");
      verify();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (state.phase === "ok") return children;

  if (state.phase === "denied-role") {
    return (
      <Shell icon={ShieldAlert} title="No permission" testid="kiosk-no-permission">
        <p>The check-in kiosk can only be opened by admins and officers. Ask an administrator if you need kiosk access.</p>
        <Link to={user?.role === "teacher" ? "/teacher" : user?.role === "driver" ? "/driver" : "/portal"} data-testid="kiosk-denied-home"
              className="mt-6 inline-flex h-11 items-center rounded-xl bg-white px-5 text-sm font-semibold text-primary">Go to my dashboard</Link>
      </Shell>
    );
  }

  if (state.phase === "checking") {
    return (
      <Shell icon={Loader2} title="Verifying location" testid="kiosk-verifying">
        <p>Confirming this device is at your location…</p>
      </Shell>
    );
  }

  const overrideBtn = state.can_override && (
    <button type="button" onClick={grantOverride} data-testid="kiosk-override-btn"
            className="inline-flex h-11 items-center gap-2 rounded-xl border border-white/40 px-5 text-sm font-semibold text-white hover:bg-white/10">
      <Unlock className="h-4 w-4" /> Override for this device
    </button>
  );
  const retryBtn = (
    <button type="button" onClick={verify} data-testid="kiosk-retry-btn"
            className="inline-flex h-11 items-center gap-2 rounded-xl bg-white px-5 text-sm font-semibold text-primary">
      <RefreshCw className="h-4 w-4" /> Retry
    </button>
  );

  return (
    <Shell icon={MapPin} title="Kiosk unavailable" testid="kiosk-off-site">
      {state.phase === "no-gps" ? (
        <p data-testid="kiosk-gps-message">Location access is needed to open the kiosk. Allow location for this site in your browser settings, then retry.</p>
      ) : state.phase === "error" ? (
        <p data-testid="kiosk-error-message">{state.message || "We could not verify this location."}</p>
      ) : (
        <p data-testid="kiosk-distance-message">
          You must be at <b>{state.location_name || "your location"}</b> to open the kiosk.
          {typeof state.distance_m === "number" && <> This device is about {state.distance_m >= 1000 ? `${(state.distance_m / 1000).toFixed(1)} km` : `${state.distance_m} m`} away (allowed: {state.radius_m} m).</>}
        </p>
      )}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">{retryBtn}{overrideBtn}</div>
    </Shell>
  );
}
